import copy from 'copy-to-clipboard'
import { compact, groupBy } from 'lodash'
import { Fragment, ReactNode, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Link } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Booking, MAX_HOURLY_DURATION } from '../../models/Booking'
import { Field } from '../../models/Field'
import { RecordFactory } from '../../models/RecordFactory'
import { Transaction } from '../../models/Transaction'
import { PERM_ROLE_CONTRACTOR } from '../../utils'
import { DOW_DATE_FORMAT, TIME_FORMAT, dateSpanDescription } from '../../utils/dates'
import { getIconNameFromType } from '../../utils/item'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Avatar from '../app/design/Avatar'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { ImageView } from '../common/ImageView'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { CustomFieldView } from '../items/CustomFieldView'
import { Receipt } from '../workflows/ReceiptView'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    booking: Booking
    customFields?: Field[]
    isWorkflowReviewStep?: boolean
}

export const BookingView = ({
    booking,
    customFields = [],
    isWorkflowReviewStep = false,
}: Props) => {
    const navigate = useNavigate()
    const { logAndDisplayError } = useErrorHandler()

    const [isShowingCheckInConfirmationModal, setIsShowingCheckInConfirmationModal] =
        useState(false)
    const [isShowingCheckOutConfirmationModal, setIsShowingCheckOutConfirmationModal] =
        useState(false)

    const forReserve =
        booking.type === 'reservation' ||
        booking.item?.type === 'reservation' ||
        (booking.duration ?? 0) > MAX_HOURLY_DURATION

    const totalPrice = (() => {
        if (booking.total !== undefined && PSApplication.me().perm <= PERM_ROLE_CONTRACTOR) {
            return 'Price Not Shown'
        }
        else if (booking.package && !booking.amount) {
            return 'Prepaid Package'
        }
        else if (!booking.rate && !booking.deposit) {
            return booking.package
                ? 'Prepaid Package'
                : booking.rate === undefined
                  ? 'Price not set'
                  : 'Free'
        }
        else if (booking.fixedScheduleCount > 1) {
            return `${currencyFormat(booking.fixedScheduleAskingAmount)}`
        }
        else {
            return `${currencyFormat(booking.askingAmount)}`
        }
    })()

    const receipt = useMemo((): Receipt => {
        const forClass = booking.item?.type === 'class'
        return {
            modifiers: {
                subtotal:
                    !forClass || booking.dropin || booking.total === 0
                        ? {
                              amount: booking.total ?? 0,
                              title: booking.forSchedule ? 'Recurring amount' : undefined,
                              subtitle:
                                  booking.forSchedule && booking.schedule?.instances
                                      ? `Total ${currencyFormat((booking.total ?? 0) * booking.schedule.instances)} (${booking.schedule.instances} sessions)`
                                      : undefined,
                          }
                        : undefined,
                deposit: !!booking.deposit
                    ? {
                          amount: booking.deposit,
                          title: !!booking.paidDepositDate
                              ? 'Paid deposit'
                              : forClass && !booking.dropin
                                ? 'Amount due'
                                : undefined,
                          subtitle:
                              forClass && !booking.dropin && !booking.paidDepositDate
                                  ? 'This amount is due upfront when the client confirms this class'
                                  : undefined,
                          isPaid: !!booking.deposit && !!booking.paidDepositDate,
                      }
                    : undefined,
                package: booking.package,
                giftCertificate: !!booking.gift
                    ? {
                          selectedGift: booking.gift,
                      }
                    : undefined,
                discount: !!booking.discount && !booking.package ? {} : undefined,
                salesTax:
                    !!booking.taxAmount || !!booking.finalTaxAmount
                        ? {
                              amount: !!booking.paidDate
                                  ? booking.taxAmount
                                  : !booking.paidDepositDate
                                    ? booking.taxAmount
                                    : booking.finalTaxAmount,
                              parenthetical: `${booking.taxcode?.rate ?? 0}%`,
                          }
                        : undefined,
                surcharge:
                    !!booking.surchargeAmount || !!booking.finalSurchargeAmount
                        ? {
                              amount: !!booking.paidDate
                                  ? booking.surchargeAmount
                                  : !!booking.paidDepositDate
                                    ? booking.finalSurchargeAmount
                                    : booking.surchargeAmount,
                          }
                        : undefined,
                gratuity: !!booking.gratuityAmount
                    ? {
                          amount: booking.gratuityAmount,
                          percent:
                              booking.gratuityType === 'percent' ? booking.gratuity : undefined,
                      }
                    : undefined,
            },
            total: booking.paidDate ? booking.chargeAmount : booking.finalChargeAmount,
            totalText: booking.paidDate ? 'Amount charged' : 'Amount to charge',
            markedPaidSourceDescription:
                !!booking.markPaidDate && !!booking.paymeth
                    ? (Transaction.payMethodStrings().get(booking.paymeth) ?? booking.paymeth)
                    : undefined,
            paymentMethodSource: booking.id !== undefined ? booking.source : undefined,
        }
    }, [booking])

    const otherDetailItems = (() => {
        const items: { id: string; title: string; content: ReactNode }[] = []

        for (const field of customFields) {
            const value = booking.fieldValues[field.name ?? '']
            if (value === undefined) {
                continue
            }

            items.push({
                id: field.id ?? '',
                title: field.label ?? '',
                content: <CustomFieldView field={field} value={value} />,
            })
        }

        return items
    })()

    const [showSnack, setShowSnack] = useState(false)
    const [snackTimeout, setSnackTimeout] = useState<NodeJS.Timeout | null>(null)

    const closeSnackbar = () => {
        if (!snackTimeout) return
        clearTimeout(snackTimeout)
        setSnackTimeout(null)
        setShowSnack(false)
    }

    const copyConferenceLink = () => {
        const urlString =
            booking.type === 'class'
                ? booking.item?.conferenceUrlString
                : booking.conferenceUrlString

        if (urlString) {
            copy(urlString)
            setShowSnack(true)

            if (snackTimeout) {
                clearTimeout(snackTimeout)
            }

            setSnackTimeout(
                setTimeout(() => {
                    setShowSnack(false)
                    setSnackTimeout(null)
                }, 3000)
            )
        }
    }

    const reservationJobs = useMemo(() => {
        if (!forReserve) {
            return null
        }

        const jobsByPerson = groupBy(booking.jobs, (job) => job.person?.id ?? '')

        return compact(
            Object.values(jobsByPerson).map((jobs) => {
                const firstJob = jobs[0]
                if (firstJob === undefined) {
                    return null
                }

                return {
                    person: firstJob.person,
                    subtitle: jobs.map((job) => job.subtypeString ?? 'Reservation').join(' / '),
                }
            })
        )
    }, [booking, forReserve])

    const [isActionButtonLoading, setIsActionButtonLoading] = useState(false)

    const handleShowCheckInConfirmationModal = () => {
        setIsShowingCheckInConfirmationModal(true)
    }

    const handleCheckIn = async () => {
        setIsActionButtonLoading(true)
        await RecordFactory.getInstance().checkinBooking(booking)
        window.location.reload()
    }

    const handleShowCheckOutConfirmationModal = () => {
        setIsShowingCheckOutConfirmationModal(true)
    }

    const handleCheckOut = async () => {
        setIsActionButtonLoading(true)
        await RecordFactory.getInstance().checkoutBooking(booking)
        window.location.reload()
    }

    const handleAutoAssign = async () => {
        setIsActionButtonLoading(true)
        const result = await RecordFactory.getInstance().assignBooking(booking)
        if (!result.ok) {
            logAndDisplayError(result.error)
            setIsActionButtonLoading(false)
            return
        }
        window.location.reload()
    }

    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center gap-6">
                        {!!booking.item && (
                            <Avatar size="xxl">
                                <ImageView
                                    fallbackIcon={{
                                        icon: `${getIconNameFromType(booking.item.type ?? '')}.large`,
                                        background: 'round',
                                        className: 'size-[116px]',
                                        svgClassName: 'size-[56px]',
                                    }}
                                    className="size-full rounded-full object-cover"
                                    image={booking.item.image}
                                />
                            </Avatar>
                        )}

                        <div className="flex flex-col items-center justify-center gap-1">
                            <Heading size="sm" className="text-center">
                                {booking.item?.name ?? booking.person?.name}
                            </Heading>

                            {forReserve ? (
                                <>
                                    {booking.checkinStartDate !== null && (
                                        <Paragraph size="lg" className="text-gray-600">
                                            {booking.checkinStartDate !== null &&
                                            booking.checkoutStartDate !== null
                                                ? dateSpanDescription(
                                                      booking.checkinStartDate,
                                                      booking.checkoutStartDate
                                                  )
                                                : booking.checkinStartDate !== null
                                                  ? booking.checkinStartDate.format('MMM D, YYYY')
                                                  : null}
                                        </Paragraph>
                                    )}

                                    {booking.checkinTimeDescription !== null && (
                                        <Paragraph size="lg" className="text-gray-600">
                                            Check-in: {booking.checkinTimeDescription}
                                        </Paragraph>
                                    )}

                                    {booking.checkoutTimeDescription !== null && (
                                        <Paragraph size="lg" className="text-gray-600">
                                            Check-out: {booking.checkoutTimeDescription}
                                        </Paragraph>
                                    )}
                                </>
                            ) : (
                                <>
                                    {!!booking.date && (
                                        <Paragraph size="lg" className="text-gray-600">
                                            {booking.date.format(DOW_DATE_FORMAT)}
                                        </Paragraph>
                                    )}
                                    {!!booking.date && (
                                        <Paragraph size="lg" className="text-gray-600">
                                            {booking.date.format(TIME_FORMAT)}
                                            {booking.endDate &&
                                            !booking.endDate.isSame(booking.date)
                                                ? ' - ' + booking.endDate.format(TIME_FORMAT)
                                                : ''}
                                        </Paragraph>
                                    )}
                                </>
                            )}

                            {booking.statusString && (
                                <Paragraph size="lg" className="font-normal text-gray-600">
                                    {booking.statusString}
                                </Paragraph>
                            )}

                            {forReserve &&
                                !!booking.id &&
                                (booking.allowCheckin ||
                                    booking.allowCheckout ||
                                    booking.allowAutoAssign) && (
                                    <div className="mt-5">
                                        {booking.allowCheckin ? (
                                            <Button
                                                intent="primary"
                                                leftIcon="calendarArrowLeft.medium"
                                                onClick={handleShowCheckInConfirmationModal}
                                                isLoading={isActionButtonLoading}
                                            >
                                                Check-in
                                            </Button>
                                        ) : booking.allowCheckout ? (
                                            <Button
                                                intent="primary"
                                                leftIcon="calendarArrowRight.medium"
                                                onClick={handleShowCheckOutConfirmationModal}
                                                isLoading={isActionButtonLoading}
                                            >
                                                Check-out
                                            </Button>
                                        ) : booking.allowAutoAssign ? (
                                            <Button
                                                intent="primary"
                                                leftIcon="user.medium"
                                                onClick={handleAutoAssign}
                                                isLoading={isActionButtonLoading}
                                            >
                                                Auto-assign
                                            </Button>
                                        ) : null}
                                    </div>
                                )}
                        </div>

                        {!isWorkflowReviewStep && booking.allowConference && (
                            <div className="flex justify-center gap-2">
                                <a href={booking.conferenceUrlString} target="PSVideo">
                                    <Button intent="primary" leftIcon="video.medium">
                                        Join video
                                    </Button>
                                </a>
                                <Button
                                    leftIcon="copy.medium"
                                    buttonClassName="text-primary-700"
                                    onClick={copyConferenceLink}
                                >
                                    Copy link
                                </Button>
                            </div>
                        )}
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-6">
                        {!!totalPrice && (
                            <BaseRecordView.IconField
                                icon="dollar.large"
                                title={totalPrice}
                                subtitle="Price"
                            />
                        )}

                        {!!booking.id &&
                            booking.item?.type === 'class' &&
                            booking.lesson &&
                            booking.graduationString && (
                                <BaseRecordView.IconField
                                    icon="cap.large"
                                    title={booking.graduationString.replace('current ', '')}
                                    subtitle={
                                        booking.owner?.industry?.type === 'pets'
                                            ? 'Graduation date'
                                            : 'Completion date'
                                    }
                                    href={`/lessons/${booking.lesson.id}`}
                                />
                            )}

                        {!!booking.schedule && (
                            <BaseRecordView.IconField
                                icon="calendar.large"
                                title={booking.schedule.description}
                                subtitle="Schedule"
                            />
                        )}

                        {!!booking.subaccount && (
                            <BaseRecordView.IconField
                                icon={
                                    <ContactAvatar
                                        user={booking.subaccount}
                                        containerClassName="row-span-2"
                                        hasHoverEffect
                                    />
                                }
                                title={booking.subaccount.name ?? ''}
                                subtitle={booking.subaccount.labelString}
                                href={`/subaccounts/${booking.subaccount.id}`}
                            />
                        )}

                        {!!booking.client && (
                            <BaseRecordView.IconField
                                icon={
                                    <ContactAvatar
                                        user={booking.client}
                                        containerClassName="row-span-2"
                                        hasHoverEffect
                                    />
                                }
                                title={booking.client.name ?? ''}
                                subtitle="Client"
                                href={`/clients/${booking.client.id}`}
                                secondaryView={
                                    !isWorkflowReviewStep ? (
                                        <Link to={`/messages/${booking.client.id}`}>
                                            <Button intent="secondary" size="small">
                                                Chat
                                            </Button>
                                        </Link>
                                    ) : undefined
                                }
                            />
                        )}
                    </div>
                </Card>

                {reservationJobs !== null ? (
                    reservationJobs.length > 0 ? (
                        <Card size="sm" title="Team members">
                            <div className="mt-6 flex flex-col items-stretch justify-start gap-6">
                                {reservationJobs.map(({ person, subtitle }) => (
                                    <BaseRecordView.IconField
                                        key={person?.id ?? ''}
                                        icon={
                                            !person ? (
                                                'user.large'
                                            ) : (
                                                <ContactAvatar
                                                    user={person}
                                                    containerClassName="row-span-2"
                                                />
                                            )
                                        }
                                        title={person?.name ?? ''}
                                        titleSize="medium"
                                        subtitle={subtitle}
                                    />
                                ))}
                            </div>
                        </Card>
                    ) : null
                ) : (
                    booking.jobs.length > 0 && (
                        <Card size="sm" title="Team members">
                            <div className="mt-6 flex flex-col items-stretch justify-start gap-6">
                                {booking.jobs.map((job, index) => (
                                    <BaseRecordView.IconField
                                        key={job.id ?? index}
                                        icon={
                                            !job.person ? (
                                                'user.large'
                                            ) : (
                                                <ContactAvatar
                                                    user={job.person}
                                                    containerClassName="row-span-2"
                                                />
                                            )
                                        }
                                        title={job.person?.name ?? ''}
                                        titleSize="medium"
                                        subtitle="Team member"
                                    />
                                ))}
                            </div>
                        </Card>
                    )
                )}

                {(booking.instructions ?? '').trim().length > 0 && (
                    <Card size="sm" title="Instructions">
                        <Paragraph className="mt-6 whitespace-pre-wrap text-gray-600">
                            {booking.instructions}
                        </Paragraph>
                    </Card>
                )}

                {!!booking.location &&
                    booking.location.length > 0 &&
                    !!booking.latitude &&
                    !!booking.longitude && (
                        <BaseRecordView.LocationSection
                            title="Location"
                            location={booking.location}
                            latLong={{
                                latitude: booking.latitude,
                                longitude: booking.longitude,
                            }}
                        />
                    )}

                {!isWorkflowReviewStep && (
                    <Card size="sm" title="Status">
                        <RecordStatusChecklist
                            items={booking.statuses.map((status) => ({
                                title: status.title,
                                subtitle: status.date?.format('MMM Do, YYYY [at] h:mm A'),
                                status: status.date !== undefined ? 'checked' : 'empty',
                            }))}
                            className="mt-6"
                        />
                    </Card>
                )}
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                {(booking.serviceLines.length > 0 || booking.productLines.length > 0) && (
                    <Card
                        title={
                            booking.serviceLines.length > 0 && booking.productLines.length > 0
                                ? 'Services & Products'
                                : booking.serviceLines.length > 0
                                  ? 'Services'
                                  : 'Products'
                        }
                    >
                        <div className="mt-8">
                            <div className="grid grid-cols-2 gap-4">
                                {booking.serviceLines.map((line) => (
                                    <div key={line.item?.id} className="flex gap-4">
                                        {!!line.item && (
                                            <Avatar className="shrink-0">
                                                <ImageView
                                                    fallbackIcon={{
                                                        icon: `${getIconNameFromType(line.item.type ?? '')}.large`,
                                                        background: 'round',
                                                    }}
                                                    className="size-full rounded-full object-cover"
                                                    image={line.item.image}
                                                />
                                            </Avatar>
                                        )}
                                        <div className="flex flex-col gap-1">
                                            <Label className="text-gray-700">
                                                {line.item?.name}
                                            </Label>

                                            <Paragraph size="sm" className="text-gray-600">
                                                {line.item?.rate === undefined
                                                    ? 'TBD'
                                                    : !line.rate
                                                      ? 'Free'
                                                      : currencyFormat(line.rate)}
                                            </Paragraph>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {booking.serviceLines.length > 0 && booking.productLines.length > 0 && (
                                <Divider className="my-4" />
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                {booking.productLines.map((line) => (
                                    <div key={line.item?.id} className="flex gap-4">
                                        {!!line.item && (
                                            <Avatar className="shrink-0">
                                                <ImageView
                                                    fallbackIcon={{
                                                        icon: `${getIconNameFromType(line.item.type ?? '')}.large`,
                                                        background: 'round',
                                                    }}
                                                    className="size-full rounded-full object-cover"
                                                    image={line.item?.image}
                                                />
                                            </Avatar>
                                        )}
                                        <div className="flex flex-col gap-1">
                                            <Label className="text-gray-700">
                                                {line.item?.name}
                                            </Label>

                                            {line.rate && (
                                                <Paragraph size="sm" className="text-gray-600">
                                                    {currencyFormat(line.rate)}
                                                    {!!line.quantity ? ` x ${line.quantity}` : ''}
                                                </Paragraph>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                )}

                {booking.total !== undefined && (
                    <Card title="Payment details">
                        <div className="mt-8">
                            <WorkflowPage.ReceiptView
                                receipt={receipt}
                                transaction={booking}
                                modifierGapSize="small"
                            />
                        </div>
                    </Card>
                )}

                {otherDetailItems.length > 0 && (
                    <Card title="Other details">
                        <div className="mt-8 flex flex-col items-stretch justify-start">
                            {otherDetailItems.map(
                                ({ id, title, content }, otherDetailItemIndex) => (
                                    <Fragment key={id}>
                                        <div className="flex flex-col items-stretch justify-center gap-y-2">
                                            <Label size="lg">{title}</Label>
                                            <Paragraph size="md" className="text-gray-600">
                                                {content}
                                            </Paragraph>
                                        </div>

                                        {otherDetailItemIndex < otherDetailItems.length - 1 && (
                                            <Divider className="my-4" />
                                        )}
                                    </Fragment>
                                )
                            )}
                        </div>
                    </Card>
                )}

                {!isWorkflowReviewStep && (
                    <BaseRecordView.NotesSection target={{ type: 'booking', booking }} />
                )}

                <WorkflowPage.FormAndContractCard
                    form={booking.form}
                    contract={booking.contract}
                    onOpenForm={
                        !isWorkflowReviewStep && booking.record
                            ? () => navigate(`/forms/${booking.record?.id}`)
                            : undefined
                    }
                    onOpenContract={
                        !isWorkflowReviewStep && booking.signature
                            ? () => navigate(`/contracts/${booking.signature?.id}`)
                            : undefined
                    }
                />

                {(booking.memo ?? '').trim().length > 0 && (
                    <Card title="Description">
                        <Paragraph className="mt-8 whitespace-pre-wrap text-gray-600">
                            {booking.memo}
                        </Paragraph>
                    </Card>
                )}

                {showSnack && (
                    // TODO: refactor into reusable Snackbar UI component
                    <div className="absolute bottom-10 left-1/2 flex translate-x-[-50%] items-center gap-4 rounded-lg bg-gray-700 p-1 pl-4 text-white shadow-lg">
                        <Icon icon="checkmark.large" shouldInheritColor />
                        <Paragraph>Join conference link copied</Paragraph>
                        <Button
                            state="dark"
                            shape="square"
                            intent="tertiary_deprecated"
                            onClick={closeSnackbar}
                        >
                            <Icon icon="close.medium" shouldInheritColor />
                        </Button>
                    </div>
                )}
            </BaseRecordView.RightColumn>

            <ConfirmationModal
                title="Check in?"
                onClose={() => setIsShowingCheckInConfirmationModal(false)}
                onConfirm={handleCheckIn}
                confirmButtonText="Check in"
                isOpen={isShowingCheckInConfirmationModal}
            >
                <Paragraph>Are you sure you want to check in this booking?</Paragraph>
            </ConfirmationModal>

            <ConfirmationModal
                title="Check out?"
                onClose={() => setIsShowingCheckOutConfirmationModal(false)}
                onConfirm={handleCheckOut}
                confirmButtonText="Check out"
                isOpen={isShowingCheckOutConfirmationModal}
            >
                <Paragraph>Are you sure you want to check out this booking?</Paragraph>
            </ConfirmationModal>
        </BaseRecordView.Container>
    )
}
